"use client";

import type { JFrogToken } from "@/api/api";
import { CopyableField } from "@/components/copyable-field";
import { DEFAULT_JFROG_TOKEN_DURATION, JFrogTokenDurationSelect } from "@/components/jfrog-token-duration-select";
import { JFrogTokenReferenceCheckbox } from "@/components/jfrog-token-reference-checkbox";
import { useServiceAccountStatus } from "@/components/service-account-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCreateJFrogToken, useJFrogTokens, useRevokeJFrogToken } from "@/hooks/use-jfrog-tokens";
import { getJFrogTokenExpiryDays, type JFrogTokenDuration } from "@/lib/jfrog-token-duration";
import { formatDate, getDaysRemaining } from "@/lib/utils";
import { useOrgStore, useUserStore } from "@/stores";
import { Check, Info, Plus, Trash2, X } from "lucide-react";
import React from "react";

type GeneratedTokenData = {
  token: string;
  referenceToken?: string;
  tokenReferenceId: string;
  expiresAt: string | null;
};

export function JFrogTokensView() {
  const user = useUserStore.use.user();
  const selectedOrgId = useOrgStore.use.selectedOrgId();
  const selectedOrg = useOrgStore.use.selectedOrg();
  const orgs = useOrgStore.use.orgs();

  const orgIdForQuery = user?.superAdmin ? selectedOrgId || undefined : selectedOrgId || user?.defaultOrg?.id;
  const selectedOrgIsAccessible = orgIdForQuery ? user?.permissions?.some((perm) => perm.id === orgIdForQuery) : false;
  const isAdmin =
    !!user &&
    (user.superAdmin ||
      (orgIdForQuery != null &&
        (user.permissions?.some(
          (perm) =>
            (selectedOrgIsAccessible ? perm.id === orgIdForQuery : perm.default) && perm.roles?.includes("ORGOWNER"),
        ) ??
          false)));

  const hasOrgAccess = !!user && (user.superAdmin || (user.organizations && user.organizations.length > 0));
  const tokensQuery = useJFrogTokens(orgIdForQuery);
  const revokeTokenMutation = useRevokeJFrogToken();
  const createTokenMutation = useCreateJFrogToken();
  const saQuery = useServiceAccountStatus(orgIdForQuery ?? "", !!orgIdForQuery);
  const [tokenDuration, setTokenDuration] = React.useState<JFrogTokenDuration>(DEFAULT_JFROG_TOKEN_DURATION);
  const [includeReferenceToken, setIncludeReferenceToken] = React.useState(false);
  const [generatedTokenData, setGeneratedTokenData] = React.useState<GeneratedTokenData | null>(null);
  const hasSA = saQuery.isSuccess && saQuery.data !== null && saQuery.data !== undefined;
  const canGenerateToken =
    !!orgIdForQuery &&
    hasSA &&
    !saQuery.isLoading &&
    !saQuery.isError &&
    !createTokenMutation.isPending &&
    !generatedTokenData;
  const jfrogUsername = saQuery.data?.jfrog_username ?? "";

  React.useEffect(() => {
    setGeneratedTokenData(null);
    setTokenDuration(DEFAULT_JFROG_TOKEN_DURATION);
    setIncludeReferenceToken(false);
  }, [orgIdForQuery]);

  if (!hasOrgAccess) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>You do not have permission to view JFrog tokens.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const handleGenerateToken = async () => {
    if (!orgIdForQuery) return;
    try {
      const result = await createTokenMutation.mutateAsync({
        orgId: orgIdForQuery,
        request: {
          organization_id: orgIdForQuery,
          expires_in_days: getJFrogTokenExpiryDays(tokenDuration),
          ...(includeReferenceToken && { include_reference_token: true }),
        },
      });
      if (result?.token_value) {
        setGeneratedTokenData({
          token: result.token_value,
          referenceToken: result.reference_token,
          tokenReferenceId: result.jfrog_token_id,
          expiresAt: result.expires_at ?? null,
        });
      }
    } catch {
      // error toast handled by the hook
    }
  };

  const handleRevoke = async (token: JFrogToken) => {
    if (!confirm(`Revoke token "${token.jfrog_token_id}"? This cannot be undone.`)) return;
    try {
      await revokeTokenMutation.mutateAsync({ orgId: token.organization_id, tokenId: token.id });
    } catch {
      // error toast handled by the hook
    }
  };

  const getStatusBadge = (token: JFrogToken) => {
    if (token.is_revoked) return <Badge variant="destructive">Revoked</Badge>;
    if (!token.expires_at)
      return (
        <Badge variant="outline" className="border-green-500 text-green-600">
          Active
        </Badge>
      );
    const daysLeft = getDaysRemaining(token.expires_at);
    if (daysLeft <= 0) return <Badge variant="destructive">Expired</Badge>;
    if (daysLeft <= 30)
      return (
        <Badge variant="outline" className="border-yellow-500 text-yellow-600">
          {daysLeft}d left
        </Badge>
      );
    return (
      <Badge variant="outline" className="border-green-500 text-green-600">
        Active
      </Badge>
    );
  };

  const isTokenManageable = (token: JFrogToken) => {
    if (token.is_revoked) return false;
    if (!token.expires_at) return true;
    return getDaysRemaining(token.expires_at) > 0;
  };

  const tokens = tokensQuery.data?.tokens ?? [];
  const isLoading = tokensQuery.isLoading;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">JFrog Registry Tokens</h1>
          <p className="text-muted-foreground">
            View registry credentials, token expiration, and manage access for pulling images from JFrog.
          </p>
          {orgs.length > 1 && selectedOrg && (
            <div className="mt-2 flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                Viewing: {selectedOrg.name}
              </Badge>
            </div>
          )}
        </div>
        <div className="flex w-full flex-col gap-3 lg:w-auto lg:min-w-[240px]">
          <JFrogTokenDurationSelect value={tokenDuration} onValueChange={setTokenDuration} />
          <JFrogTokenReferenceCheckbox
            checked={includeReferenceToken}
            onCheckedChange={setIncludeReferenceToken}
            disabled={!canGenerateToken}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block">
                <Button onClick={handleGenerateToken} disabled={!canGenerateToken} className="w-full">
                  <Plus className="mr-2 h-4 w-4" />
                  {createTokenMutation.isPending ? "Generating..." : "Generate Token"}
                </Button>
              </span>
            </TooltipTrigger>
            {!hasSA && saQuery.isSuccess && (
              <TooltipContent>No service account configured for this organization</TooltipContent>
            )}
            {saQuery.isLoading && <TooltipContent>Checking service account status...</TooltipContent>}
            {saQuery.isError && <TooltipContent>Unable to verify service account status</TooltipContent>}
          </Tooltip>
        </div>
      </div>

      {jfrogUsername && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Registry username</CardTitle>
            <CardDescription>Use this username with your JFrog registry token when authenticating.</CardDescription>
          </CardHeader>
          <CardContent>
            <CopyableField label="Username" value={jfrogUsername} mono />
          </CardContent>
        </Card>
      )}

      {!saQuery.isLoading && saQuery.isSuccess && !hasSA && (
        <Alert variant="default" className="border-blue-500/30 bg-blue-50/50 dark:bg-blue-950/20">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-800 dark:text-blue-200">
            A superadmin must create a service account for this organization before tokens can be generated.
          </AlertDescription>
        </Alert>
      )}

      {saQuery.isError && (
        <Alert variant="default" className="border-destructive/30 bg-destructive/10">
          <Info className="h-4 w-4 text-destructive" />
          <AlertDescription className="text-destructive">
            Unable to verify service account status. Token generation is disabled until status can be confirmed.
          </AlertDescription>
        </Alert>
      )}

      {generatedTokenData && (
        <Alert className="border-green-500/50 bg-green-50 dark:bg-green-950/20">
          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            <div className="flex items-center justify-between">
              <span>
                Token generated — copy these credentials now. The token values will not be shown again.
                {generatedTokenData.expiresAt && (
                  <span className="block text-xs opacity-80">
                    Valid for {getDaysRemaining(generatedTokenData.expiresAt)} days (expires{" "}
                    {formatDate(generatedTokenData.expiresAt)})
                  </span>
                )}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => setGeneratedTokenData(null)}
                title="Dismiss"
                aria-label="Dismiss"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <div className="mt-4 space-y-3">
              {jfrogUsername && <CopyableField label="Username" value={jfrogUsername} mono />}
              <CopyableField label="Token ID" value={generatedTokenData.tokenReferenceId} mono />
              {generatedTokenData.referenceToken && (
                <CopyableField label="Reference token" value={generatedTokenData.referenceToken} mono />
              )}
              <CopyableField label="Token" value={generatedTokenData.token} mono />
              {generatedTokenData.expiresAt && (
                <CopyableField label="Expires" value={formatDate(generatedTokenData.expiresAt)} />
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isLoading ? (
              <Skeleton className="h-4 w-32" />
            ) : (
              `${tokens.length} token${tokens.length !== 1 ? "s" : ""} in this organization`
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : tokens.length === 0 ? (
            <div className="flex flex-col items-center gap-4 py-12 text-center">
              <p className="text-muted-foreground">
                {saQuery.isLoading
                  ? "Checking service account status..."
                  : saQuery.isError
                    ? "Unable to verify service account status. Token generation is disabled until status can be confirmed."
                    : hasSA
                      ? "No JFrog tokens found. Generate your first token to start pulling images."
                      : "No service account configured for this organization. A superadmin must create one before tokens can be generated."}
              </p>
              {hasSA && (
                <Button onClick={handleGenerateToken} disabled={!canGenerateToken}>
                  <Plus className="mr-2 h-4 w-4" />
                  {createTokenMutation.isPending ? "Generating..." : "Generate Token"}
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Token ID</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin && <TableHead className="w-12" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokens.map((token) => (
                  <TableRow key={token.jfrog_token_id}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{token.jfrog_token_id}</TableCell>
                    <TableCell className="text-sm">{token.created_by_user_email}</TableCell>
                    <TableCell className="text-sm">{formatDate(token.created_at)}</TableCell>
                    <TableCell className="text-sm">
                      {token.expires_at ? (
                        <div>
                          <div>{formatDate(token.expires_at)}</div>
                          <div className="text-xs text-muted-foreground">
                            {getDaysRemaining(token.expires_at)}d left
                          </div>
                        </div>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(token)}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        {isTokenManageable(token) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Revoke token"
                                onClick={() => handleRevoke(token)}
                                disabled={revokeTokenMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Revoke token</TooltipContent>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
